import { Injectable, OnDestroy } from '@angular/core';
import { StorageSyncType, WindowRef } from '@spartacus/core';
import { Observable, of, Subscription } from 'rxjs';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { getStorage, persistToStorage, readFromStorage } from './browser-storage';

@Injectable()
export class StatePersistenceService implements OnDestroy {
  subscription = new Subscription();
  constructor(protected winRef: WindowRef) { }


  /**
   * Helper to synchronize state to more persistent storage (localStorage, sessionStorage).
   * It is context aware, so you can keep different state for te same feature based on specified context.
   *
   * Eg. cart is valid only under the same base site. So you want to synchronize cart only with the same base site.
   * Usage for that case: `syncWithStorage({ key: 'cart', state$: activeCartSelector$, context$: this.siteContextParamsService.getValues([BASE_SITE_CONTEXT_ID]), onRead: (state) => setCorrectStateInStore(state) })`.
   * Active cart for the `electronics` base site will be stored under `spartacus⚿electronics⚿cart` and for apparel under `spartacus⚿apparel⚿cart`.
   *
   * On each context change onRead function will be executed with state from storage provided as a parameter.
   *
   * Omitting context$ will trigger onRead only once at initialization.
   *
   * @param key Key to use in storage for the synchronized state. Should be unique for each feature.
   * @param state$ State to be saved and later restored.
   * @param context$ Context for state
   * @param storageType Storage type to be used to persist state
   * @param onRead Function to be executed on each storage read after context change
   *
   * @returns Subscriptions for reading/writing in storage on context/state change
   */
  syncWithStorage<T>({
    key,
    state$,
    context$ = of(''),
    storageType = StorageSyncType.LOCAL_STORAGE,
    onRead = () => {
      // Intentional empty arrow function
    },
  }: {
    key: string;
    state$: Observable<T>;
    context$?: Observable<string | Array<string>>;
    storageType?: StorageSyncType;
    onRead?: (stateFromStorage: T | undefined) => void;
  }): Subscription {
    const storage = getStorage(storageType, this.winRef);


    // Do not change order of subscription! Read should happen before write on context change.
    this.subscription.add(
      context$
        .pipe(
          map((context) => {
            return storage
              ? (readFromStorage(storage, this.generateKeyWithContext(context, key)) as T | undefined)
              : undefined;
          }),
          tap((state) => onRead(state))
        )
        .subscribe()
    );

    if (storage) {
      this.subscription.add(
        state$.pipe(withLatestFrom(context$)).subscribe(([state, context]) => {
          persistToStorage(this.generateKeyWithContext(context, key), state, storage);
        })
      );
    }
    return this.subscription;
  }

  /**
   * Helper to read state from persistent storage (localStorage, sessionStorage).
   * It is useful if you need synchronously access state saved with `syncWithStorage`.
   *
   * @param key Key to use in storage for state. Should be unique for each feature.
   * @param context Context value for state
   * @param storageType Storage type from to read state
   *
   * @returns State from the storage
   */
  readStateFromStorage<T>({
    key,
    context = '',
    storageType = StorageSyncType.LOCAL_STORAGE,
  }: {
    key: string;
    context?: string | Array<string>;
    storageType?: StorageSyncType;
  }): T | undefined {
    const storage = getStorage(storageType, this.winRef);

    if (storage) {
      return readFromStorage(storage, this.generateKeyWithContext(context, key)) as T | undefined;
    } else {
      return undefined;
    }
  }

  protected generateKeyWithContext(context: string | Array<string>, key: string): string {
    return `spartacus⚿${([] as Array<string>).concat(context).join('⚿')}⚿${key}`;
  }
  ngOnDestroy(): void {

    this.subscription.unsubscribe();

  }
}
